import './App.css';
import { useEffect, useState } from 'react';

function encodeFormData(data) {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&');
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="1" y="5" width="22" height="14" rx="4" fill="#FF0000" />
      <polygon points="10,8.5 16,12 10,15.5" fill="#FFFFFF" />
    </svg>
  );
}

function AppleMusicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <defs>
        <linearGradient id="appleMusicGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FA57C1" />
          <stop offset="100%" stopColor="#FB5C74" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#appleMusicGradient)" />
      <path d="M11 16.5V7.8L18 6.3v8.2" fill="none" stroke="#FFFFFF" strokeWidth="1.5" />
      <circle cx="9" cy="16.5" r="2" fill="#FFFFFF" />
      <circle cx="16" cy="14.9" r="2" fill="#FFFFFF" />
    </svg>
  );
}

function SpotifyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#1DB954" />
      <path d="M6.5 9.5c4-1.2 8-1 11 .6" fill="none" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.8 12.8c3.3-1 6.7-.8 9.4.6" fill="none" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M7.2 15.8c2.6-.7 5.2-.6 7.3.5" fill="none" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function App() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isContactPopupVisible, setIsContactPopupVisible] = useState(false);
  const [isContactPopupDismissed, setIsContactPopupDismissed] = useState(false);
  const [contactForm, setContactForm] = useState({ firstName: '', lastName: '', email: '', enquiry: '' });
  const [contactStatus, setContactStatus] = useState('idle'); // idle | submitting | success | error

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactStatus('submitting');
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encodeFormData({ 'form-name': 'contact', ...contactForm }),
    })
      .then(() => setContactStatus('success'))
      .catch(() => setContactStatus('error'));
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (!element) return;
    const headerOffset = 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };

  const [enquiryForm, setEnquiryForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    studentAge: '',
    pianoExperience: '',
    lessonType: '',
    message: '',
  });
  const [enquiryStatus, setEnquiryStatus] = useState('idle'); // idle | submitting | success | error

  const handleEnquiryChange = (e) => {
    const { name, value } = e.target;
    setEnquiryForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEnquirySubmit = (e) => {
    e.preventDefault();
    setEnquiryStatus('submitting');
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encodeFormData({ 'form-name': 'contact-enquiry', ...enquiryForm }),
    })
      .then(() => setEnquiryStatus('success'))
      .catch(() => setEnquiryStatus('error'));
  };

  useEffect(() => {
    const header = document.querySelector('.main-header');
    const heroSection = document.querySelector('.hero-section');
    const heroHeight = heroSection ? heroSection.offsetHeight : 0;
    const fadeElements = document.querySelectorAll('.fade-in');

    // Scroll handler for header with hero section detection, throttled to one run per frame
    let ticking = false;
    const updateOnScroll = () => {
      const scrollY = window.scrollY;

      const isScrolled = scrollY > 50;
      header.classList.toggle('scrolled', isScrolled);
      header.classList.toggle('hero-header', !isScrolled && scrollY < heroHeight * 0.8);

      fadeElements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        if (elementTop < window.innerHeight - 150) {
          element.classList.add('visible');
        }
      });

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateOnScroll);
        ticking = true;
      }
    };

    // Modified scroll handler with special case for About section
    const handleNavClick = (e) => {
      if (e.target.hash) {
        e.preventDefault();
        const element = document.querySelector(e.target.hash);
        let headerOffset = 100; // Default offset for most sections

        // Special larger offset just for the About section
        if (e.target.hash === '#about') {
          headerOffset = -10; // Increased offset for About section
        }

        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    // Modified header click handler
    const handleHeaderClick = (e) => {
      // Only trigger if clicking the header area, not the nav links
      if (!e.target.closest('nav')) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.querySelectorAll('nav a').forEach(anchor => {
      anchor.addEventListener('click', handleNavClick);
    });

    // Add click listener to entire header instead of just h1
    header.addEventListener('click', handleHeaderClick);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.querySelectorAll('nav a').forEach(anchor => {
        anchor.removeEventListener('click', handleNavClick);
      });
      header.removeEventListener('click', handleHeaderClick);
    };
  }, []);

  // Add effect to handle video loading
  useEffect(() => {
    if (isVideoOpen) {
      // Add your video loading logic here
    }
  }, [isVideoOpen]);

  // Show the "Get in Touch" popup once the visitor scrolls near the bottom of the About section
  useEffect(() => {
    if (isContactPopupDismissed) return;

    const handleContactScroll = () => {
      const aboutSection = document.querySelector('.about-section');
      if (!aboutSection) return;
      const rect = aboutSection.getBoundingClientRect();
      if (rect.bottom < window.innerHeight * 0.9) {
        setIsContactPopupVisible(true);
      }
    };

    window.addEventListener('scroll', handleContactScroll);
    handleContactScroll();
    return () => window.removeEventListener('scroll', handleContactScroll);
  }, [isContactPopupDismissed]);

  return (
    <div className="App">
      <header className="main-header">
        <h1 style={{color: '#ffffff', fontWeight: '700', fontSize: '2rem'}}>
          <span className="cursive-text" style={{color: 'rgba(255, 255, 255, 0.85)'}}>MG</span>
          <span className="regular-text" style={{color: 'rgba(255, 255, 255, 0.85)'}}>&nbsp;Music Academy</span>
        </h1>
        <nav>
          <ul>
            <li><a href="#hero" style={{color: '#ffffff', fontWeight: '500'}}>Home</a></li>
            <li><a href="#about" style={{color: '#ffffff', fontWeight: '500'}}>About</a></li>
            <li><a href="#schedule" style={{color: '#ffffff', fontWeight: '500'}}>Lessons</a></li>
            <li><a href="#contact" style={{color: '#ffffff', fontWeight: '500'}}>Contact</a></li>
          </ul>
        </nav>
      </header>

      <main>
        <section id="hero" className="hero-section">
          <div className="hero-content">
            <div className="hero-image">
              <img src="/piano-background.png" alt="Piano" />
            </div>
            <div className="hero-text">
              <h2>Private Piano Lessons</h2>
              <h2>Classical Piano Training</h2>
              <p>Rushcutters Bay, Sydney · Online</p>
              <button
                type="button"
                className="hero-trial-button"
                onClick={scrollToContact}
              >
                Request a Trial
              </button>
            </div>
          </div>
        </section>

        <section id="about" className="about-section">
          <div className="about-content">
            <div className="about-text fade-in">
              <h2>About Mariia</h2>
              <p>With over two decades of experience in piano, Mariia Gushchina brings a rich blend of classical training and Russian teaching methods to her lessons, creating a vibrant and inspiring environment for students.</p>
              <p>Mariia's journey has taken her through Moscow, Madrid, London, and New York, where she immersed herself in diverse cultural and musical influences. These experiences have shaped her distinctive teaching approach, balancing technical precision and creative expression.</p>
              <p>Her philosophy is rooted in the belief that piano is not just a skill but a source of profound personal growth and therapeutic value. Mariia is passionate about helping students of all ages discover their own musical voices while nurturing a deep appreciation for the timeless beauty of music </p>
              <ul className="credentials">
                <li>Graduated Russian Piano School</li>
                <li>20+ Years Piano Experience</li>
                <li>International Festival Winner</li>
                <li>Trinity College London Grade 8 Piano — Distinction</li>
                <li>ARSM Diploma (ABRSM) — Distinction</li>
              </ul>
            </div>
            <div className="about-image fade-in">
              <img src="/mariia-photo.jpg" alt="Mariia Gushchina teaching piano" />
            </div>
          </div>
          <div className="about-actions">
            <button
              className="video-link"
              onClick={() => setIsVideoOpen(true)}
            >
              <YouTubeIcon />
              Watch Mariia Play
            </button>
            <a
              className="video-link"
              href="https://music.apple.com/us/artist/mariia-gushchina/1896554999"
              target="_blank"
              rel="noopener noreferrer"
            >
              <AppleMusicIcon />
              Listen on Apple Music
            </a>
            <a
              className="video-link"
              href="https://open.spotify.com/artist/6jFG8MsdK4dsZEhEuDJkof?si=Bd13onCuQb6umQfDMu_sAA"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SpotifyIcon />
              Listen on Spotify
            </a>
          </div>
        </section>

        {/* Video Modal */}
        {isVideoOpen && (
          <div className="video-modal" onClick={() => setIsVideoOpen(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button 
                className="close-button"
                onClick={() => setIsVideoOpen(false)}
              >
                ×
              </button>
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/kAb5fjgNB9I?autoplay=1"
                title="Mariia Playing Piano"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}

        <section id="schedule" className="schedule-section">
          <h2>Private Piano Lessons</h2>
          <p className="schedule-subheader">In-person in Rushcutters Bay or online</p>
          <p className="schedule-description">
            Lessons are individually structured according to the student's age, experience and goals, with 60 minutes as the standard lesson, and shorter lessons available where appropriate.
          </p>

          <div className="schedule-trial-card">
            <h3>Complimentary 30-Minute Trial</h3>
            <p>An opportunity to meet, discuss your musical goals and determine the most suitable approach to lessons.</p>
            <button
              type="button"
              className="schedule-button"
              onClick={scrollToContact}
            >
              Request a Trial
            </button>
          </div>
        </section>


        <section id="pricing" className="pricing-section">
          <h2>Teaching Approach</h2>
          <p className="pricing-subheader">Individual classical piano instruction for children and adults.</p>

          <div className="lessons-overview">
            <div className="lessons-intro-text">
              <h3>An Individual Approach</h3>
              <p>Lessons are shaped around each student's experience, goals and way of learning.</p>
              <p className="personal-note">"Every student is unique, as is their own connection with music. My approach is based on finding a comfortable pace for each student while building confidence and a solid musical foundation. With thoughtful, individual guidance, students are encouraged to explore what music means to them, gradually developing their relationship with the instrument and discovering a musical voice of their own."</p>
              <p className="personal-note-attribution">- Mariia G</p>
            </div>
            <div className="lessons-during">
              <h3>During Lessons</h3>
              <div className="lessons-during-item">
                <h4>Technique &amp; Foundation</h4>
                <p>Posture, hand position, coordination, touch and healthy technique are developed naturally through repertoire and focused exercises.</p>
              </div>
              <div className="lessons-during-item">
                <h4>Reading &amp; Musicianship</h4>
                <p>Students develop confident music reading, rhythm, listening and an understanding of the musical language behind what they play.</p>
              </div>
              <div className="lessons-during-item">
                <h4>Repertoire</h4>
                <p>Pieces are chosen thoughtfully according to the student's level, interests and development, with classical repertoire forming the foundation of study.</p>
              </div>
              <div className="lessons-during-item">
                <h4>Musical Understanding</h4>
                <p>Students are encouraged to listen closely, understand the character of a piece and gradually form their own musical ideas.</p>
              </div>
            </div>
          </div>

          <div className="testimonial">
            <p className="testimonial-stars">★★★★★</p>
            <p className="testimonial-quote">"Maria is an amazing person and piano teacher. My daughter really enjoys her lessons! She is patient, and adjusts the lessons to what the necessities are. Highly recommended!"</p>
            <p className="testimonial-author">— George K.</p>
          </div>

          <div className="who-i-teach">
            <h3>Who I Teach</h3>
            <div className="who-i-teach-grid">
              <div className="who-i-teach-item">
                <h4>Young Beginners</h4>
                <p>Children beginning their musical education, with an emphasis on healthy technique, reading and strong foundations.</p>
              </div>
              <div className="who-i-teach-item">
                <h4>Developing Students</h4>
                <p>Students working through the early and intermediate stages of piano study, including preparation up to approximately Grade 7 level.</p>
              </div>
              <div className="who-i-teach-item">
                <h4>Adult Students</h4>
                <p>Beginners, returning pianists and adults wishing to develop their technique, repertoire and musical understanding.</p>
              </div>
            </div>
          </div>

          <div className="pricing-formats-band">
            <h3 className="lessons-choose-heading">Choose Your Lesson Format</h3>
            <div className="pricing-cards">
              <div className="pricing-card featured">
                <h3>60-Minute Lessons</h3>
                <p className="pricing-description">Standard format for adults and intermediate students.</p>
              </div>

              <div className="pricing-card">
                <h3>45-Minute Lessons</h3>
                <p className="pricing-description">A shorter format for students requiring focused technical and musical work.</p>
              </div>

              <div className="pricing-card">
                <h3>30-Minute Lessons</h3>
                <p className="pricing-description">Primarily for younger beginners.</p>
              </div>
            </div>

            <button
              type="button"
              className="schedule-button request-trial-cta"
              onClick={scrollToContact}
            >
              Request a Lesson
            </button>

            <div className="pricing-help">
              <h3>Not sure which lesson length is appropriate?</h3>
              <p>Lesson duration can be discussed following your complimentary trial lesson.</p>
            </div>

            <p className="pricing-note">All in-person lessons include complimentary tea service.</p>
          </div>
        </section>

        <section id="contact" className="contact-section">
          <div className="contact-two-col">
            <div className="contact-info-col">
              <h2>Get in Touch</h2>
              <p className="contact-intro">Whether you're interested in beginning lessons or simply have a question, I'd be happy to hear from you.</p>

              <div className="contact-info-block">
                <h4>Private studio</h4>
                <p>Rushcutters Bay, Sydney</p>
              </div>
              <div className="contact-info-block">
                <h4>Lessons</h4>
                <p>In-person &amp; online</p>
              </div>
              <div className="contact-info-block">
                <h4>Email</h4>
                <p><a href="mailto:mariiamusicacademy@gmail.com">mariiamusicacademy@gmail.com</a></p>
              </div>
            </div>

            <div className="contact-form-col">
              {enquiryStatus === 'success' ? (
                <div className="trial-form-success">
                  <h3>Thank You!</h3>
                  <p>Your enquiry has been sent. I'll be in touch soon.</p>
                </div>
              ) : (
                <>
                  <h3 className="enquiry-eyebrow">Lesson Enquiry</h3>
                  <form
                    name="contact-enquiry"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleEnquirySubmit}
                    className="trial-form enquiry-form"
                  >
                    <input type="hidden" name="form-name" value="contact-enquiry" />
                    <p className="hidden-field">
                      <label>Don't fill this out: <input name="bot-field" /></label>
                    </p>

                    <label className="enquiry-label">Name</label>
                    <div className="enquiry-form-row">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="First name"
                        value={enquiryForm.firstName}
                        onChange={handleEnquiryChange}
                        required
                      />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Last name"
                        value={enquiryForm.lastName}
                        onChange={handleEnquiryChange}
                        required
                      />
                    </div>

                    <label className="enquiry-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email address"
                      value={enquiryForm.email}
                      onChange={handleEnquiryChange}
                      required
                    />

                    <label className="enquiry-label">Student age (optional)</label>
                    <input
                      type="text"
                      name="studentAge"
                      placeholder="Age"
                      value={enquiryForm.studentAge}
                      onChange={handleEnquiryChange}
                    />

                    <label className="enquiry-label">Piano experience</label>
                    <select
                      name="pianoExperience"
                      value={enquiryForm.pianoExperience}
                      onChange={handleEnquiryChange}
                      required
                    >
                      <option value="" disabled>Select</option>
                      <option value="New to piano">New to piano</option>
                      <option value="Beginner">Beginner</option>
                      <option value="Some previous experience">Some previous experience</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Returning to piano">Returning to piano</option>
                      <option value="Not sure">Not sure</option>
                    </select>

                    <label className="enquiry-label">Lesson preference</label>
                    <select
                      name="lessonType"
                      value={enquiryForm.lessonType}
                      onChange={handleEnquiryChange}
                      required
                    >
                      <option value="" disabled>Select</option>
                      <option value="In person - Rushcutters Bay">In person — Rushcutters Bay</option>
                      <option value="Online">Online</option>
                      <option value="Not sure yet">Not sure yet</option>
                    </select>

                    <label className="enquiry-label">Tell me a little about what you're looking for</label>
                    <textarea
                      name="message"
                      placeholder="Tell me a little about yourself or the student..."
                      rows="4"
                      value={enquiryForm.message}
                      onChange={handleEnquiryChange}
                      required
                    ></textarea>

                    <button type="submit" className="schedule-button request-trial-cta" disabled={enquiryStatus === 'submitting'}>
                      {enquiryStatus === 'submitting' ? 'Sending...' : 'Send Enquiry'}
                    </button>
                    <p className="enquiry-response-note">I'll usually respond within one business day.</p>
                    {enquiryStatus === 'error' && (
                      <p className="trial-form-error">Something went wrong. Please try again.</p>
                    )}
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      
      <footer className="site-footer">
        <p>© 2026 MG Music Academy · Sydney, NSW</p>
      </footer>

      {/* Get in Touch popup */}
      {isContactPopupVisible && !isContactPopupDismissed && (
        <div className="contact-popup">
          <button
            className="contact-popup-close"
            onClick={() => {
              setIsContactPopupDismissed(true);
              setIsContactPopupVisible(false);
            }}
            aria-label="Close"
          >
            ×
          </button>

          {contactStatus === 'success' ? (
            <div className="contact-popup-success">
              <h3>Thank You</h3>
              <p>Your message has been sent. I'll be in touch soon.</p>
            </div>
          ) : (
            <>
              <h3>Get in Touch</h3>
              <form
                name="contact"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleContactSubmit}
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden-field">
                  <label>Don't fill this out: <input name="bot-field" /></label>
                </p>
                <div className="contact-popup-row">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={contactForm.firstName}
                    onChange={handleContactChange}
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={contactForm.lastName}
                    onChange={handleContactChange}
                    required
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                  required
                />
                <textarea
                  name="enquiry"
                  placeholder="Your Enquiry"
                  rows="3"
                  value={contactForm.enquiry}
                  onChange={handleContactChange}
                  required
                ></textarea>
                <button type="submit" className="contact-popup-submit" disabled={contactStatus === 'submitting'}>
                  {contactStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
                {contactStatus === 'error' && (
                  <p className="contact-popup-error">Something went wrong. Please try again.</p>
                )}
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default App;